import { motion } from "framer-motion";

/**
 * AnimatedSoundWave
 * A pure-SVG/CSS animated sound-wave centerpiece using the brand palette.
 * - 3 layered sine waves (violet → blue → magenta) drifting at different speeds
 * - Concentric pulse rings + EQ bars + waveform reflections
 * - No external image; lightweight and scales to any size.
 */
const AnimatedSoundWave = ({
  className = "",
  "data-testid": testId,
  compact = false,
  centerNode = null,
}) => {
  // Generate sine path for a given amplitude / freq / phase
  const wavePath = (amp, freq, phase, yOffset = 100) => {
    const points = [];
    for (let x = 0; x <= 400; x += 4) {
      const y =
        yOffset +
        Math.sin((x / 400) * Math.PI * 2 * freq + phase) * amp;
      points.push(`${x === 0 ? "M" : "L"} ${x} ${y}`);
    }
    return points.join(" ");
  };

  return (
    <div
      className={`relative aspect-square ${className}`}
      data-testid={testId}
      aria-hidden
    >
      {/* outer glow halos */}
      <div className="absolute inset-0 rounded-full bg-[#00f0ff]/10 blur-[80px]" />
      <div className="absolute inset-8 rounded-full bg-[#6b21d9]/25 blur-[100px]" />
      <div className="absolute inset-20 rounded-full bg-[#ff0055]/15 blur-[80px]" />

      {/* concentric pulse rings */}
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="absolute inset-0 rounded-full border"
          style={{
            borderColor:
              i === 0
                ? "rgba(0, 240, 255, 0.4)"
                : i === 1
                  ? "rgba(107, 33, 217, 0.35)"
                  : "rgba(255, 0, 85, 0.3)",
          }}
          animate={{
            scale: [0.7, 1.15],
            opacity: [0.7, 0],
          }}
          transition={{
            duration: 3.6,
            repeat: Infinity,
            ease: "easeOut",
            delay: i * 1.2,
          }}
        />
      ))}

      {/* central disc with conic gradient ring */}
      <div className="absolute inset-[18%] rounded-full overflow-hidden">
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "conic-gradient(from 0deg, #00f0ff, #6b21d9, #ff0055, #6b21d9, #00f0ff)",
            animation: "spin-conic 14s linear infinite",
            filter: "blur(2px)",
            opacity: 0.55,
          }}
        />
        <div className="absolute inset-[6%] rounded-full bg-[#05050a]/85 backdrop-blur-sm" />
        {/* gridlines */}
        <div
          className="absolute inset-[6%] rounded-full opacity-20"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.4) 0 1px, transparent 1px 18px), repeating-linear-gradient(90deg, rgba(255,255,255,0.4) 0 1px, transparent 1px 18px)",
          }}
        />

        {/* SVG sine waves */}
        <svg
          viewBox="0 0 400 200"
          className="absolute inset-[6%] w-[88%] h-[88%]"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="wave-cyan" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#00f0ff" stopOpacity="0" />
              <stop offset="50%" stopColor="#00f0ff" stopOpacity="1" />
              <stop offset="100%" stopColor="#00f0ff" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="wave-violet" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#a855f7" stopOpacity="0" />
              <stop offset="50%" stopColor="#a855f7" stopOpacity="1" />
              <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="wave-magenta" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ff0055" stopOpacity="0" />
              <stop offset="50%" stopColor="#ff0055" stopOpacity="1" />
              <stop offset="100%" stopColor="#ff0055" stopOpacity="0" />
            </linearGradient>
            <filter id="wave-glow">
              <feGaussianBlur stdDeviation="2.5" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* phase animator using SMIL-like CSS transform via keyframes */}
          <g style={{ animation: "wave-shift-a 6s linear infinite" }}>
            <path
              d={wavePath(28, 2, 0, 100)}
              fill="none"
              stroke="url(#wave-cyan)"
              strokeWidth="2.5"
              strokeLinecap="round"
              filter="url(#wave-glow)"
            />
          </g>
          <g
            style={{ animation: "wave-shift-b 8s linear infinite" }}
            opacity="0.9"
          >
            <path
              d={wavePath(36, 1.5, Math.PI / 2, 100)}
              fill="none"
              stroke="url(#wave-violet)"
              strokeWidth="2"
              strokeLinecap="round"
              filter="url(#wave-glow)"
            />
          </g>
          <g
            style={{ animation: "wave-shift-c 5s linear infinite" }}
            opacity="0.85"
          >
            <path
              d={wavePath(20, 3, Math.PI, 100)}
              fill="none"
              stroke="url(#wave-magenta)"
              strokeWidth="1.8"
              strokeLinecap="round"
              filter="url(#wave-glow)"
            />
          </g>

          {/* center mid line */}
          <line
            x1="0"
            y1="100"
            x2="400"
            y2="100"
            stroke="rgba(255,255,255,0.08)"
            strokeDasharray="2 4"
          />
        </svg>
      </div>

      {/* radial EQ bars overlay */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="relative w-[68%] h-[68%]">
          {Array.from({ length: 64 }).map((_, i) => {
            const angle = (i / 64) * 360;
            const isAccent = i % 8 === 0;
            return (
              <span
                key={i}
                className="absolute left-1/2 top-1/2 origin-bottom"
                style={{
                  transform: `translate(-50%, -100%) rotate(${angle}deg) translateY(-46%)`,
                  height: isAccent ? "14%" : "8%",
                  width: isAccent ? "3px" : "2px",
                  background: isAccent
                    ? "linear-gradient(180deg, #00f0ff, #ff0055)"
                    : "rgba(255,255,255,0.6)",
                  borderRadius: "2px",
                  animation: `eq-radial ${(1.2 + (i % 7) * 0.13).toFixed(2)}s ease-in-out ${(i * 0.03).toFixed(2)}s infinite alternate`,
                  opacity: isAccent ? 1 : 0.55,
                  boxShadow: isAccent ? "0 0 8px #00f0ff" : "none",
                }}
              />
            );
          })}
        </div>
      </div>

      {/* center — dot by default, or a caller-supplied node (e.g. a podcast mic) */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        {centerNode ? (
          <div className="relative">{centerNode}</div>
        ) : (
          <div className="relative">
            <div className="w-3 h-3 rounded-full bg-white shadow-[0_0_20px_#00f0ff]" />
            <div className="absolute inset-0 w-3 h-3 rounded-full bg-[#00f0ff]/60 animate-ping" />
          </div>
        )}
      </div>

      {/* corner micro labels for tech feel — hidden in compact (chat launcher) mode */}
      {!compact && (
        <>
          <div className="absolute top-[8%] left-[10%] text-[9px] font-mono-tight uppercase tracking-[0.32em] text-[#00f0ff]/70">
            STAGE A
          </div>
          <div className="absolute top-[8%] right-[10%] text-[9px] font-mono-tight uppercase tracking-[0.32em] text-[#ff0055]/70">
            ● REC
          </div>
          <div className="absolute bottom-[8%] left-[10%] text-[9px] font-mono-tight uppercase tracking-[0.32em] text-white/40">
            48k · 24-bit
          </div>
          <div className="absolute bottom-[8%] right-[10%] text-[9px] font-mono-tight uppercase tracking-[0.32em] text-white/40">
            −12dB
          </div>
        </>
      )}
    </div>
  );
};

export default AnimatedSoundWave;
